import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/ui';
import { CheckmarkIcon } from '@/icons';
import { CommissionTab, findCategoryPath } from '../../helpers';
import { useCommissionCategoriesTab } from '../../hooks/useCommissionCategoriesTab';
import CommissionCategoriesTabContainer from '../CommissionCategoriesTabContainer/CommissionCategoriesTabContainer';
import CommissionHistoryTabContainer from '../CommissionHistoryTabContainer/CommissionHistoryTabContainer';
import {
  StyledContentBlock,
  StyledHeaderRow,
  StyledMainTitleWrapper,
  StyledMenu,
  StyledPageDescription,
  StyledPageTitle,
  StyledPageWrapper,
} from './CommissionMainContainerStyles';

const CATEGORY_QUERY_KEY = 'category';

const CommissionMainContainer = () => {
  const { t } = useTranslation(['commission']);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<CommissionTab>(CommissionTab.CATEGORIES);
  const {
    categories,
    editedValues,
    handleCommissionChange,
    handleSave,
    isSaving,
    hasInvalidValues,
    canSave,
    canManage,
    isLoading,
  } = useCommissionCategoriesTab();

  const categoryFromQuery = searchParams.get(CATEGORY_QUERY_KEY);

  const selectedPath = useMemo(() => {
    if (!categoryFromQuery) {
      return [];
    }

    return findCategoryPath(categories, categoryFromQuery);
  }, [categories, categoryFromQuery]);

  const selectedId = selectedPath.length > 0 ? categoryFromQuery : null;

  const forcedOpenIds = useMemo(() => {
    if (selectedPath.length <= 1) {
      return new Set<string>();
    }

    return new Set(selectedPath.slice(0, -1));
  }, [selectedPath]);

  const handleSelect = useCallback(
    (categoryId: string) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set(CATEGORY_QUERY_KEY, categoryId);
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const tabItems = useMemo(
    () => [
      { key: CommissionTab.CATEGORIES, label: t('tab_categories') },
      { key: CommissionTab.HISTORY, label: t('tab_history') },
    ],
    [t],
  );

  return (
    <StyledPageWrapper>
      <StyledMainTitleWrapper>
        <StyledHeaderRow>
          <div>
            <StyledPageTitle>{t('page_title')}</StyledPageTitle>
            <StyledPageDescription>{t('page_description')}</StyledPageDescription>
          </div>
          {activeTab === CommissionTab.CATEGORIES && canSave && (
            <Button
              type="primary"
              icon={<CheckmarkIcon />}
              loading={isSaving}
              disabled={hasInvalidValues}
              onClick={handleSave}
            >
              {t('save_button')}
            </Button>
          )}
        </StyledHeaderRow>
        <StyledMenu
          mode="horizontal"
          items={tabItems}
          selectedKeys={[activeTab]}
          onClick={({ key }) => setActiveTab(key as CommissionTab)}
        />
      </StyledMainTitleWrapper>

      <StyledContentBlock>
        {activeTab === CommissionTab.CATEGORIES && (
          <CommissionCategoriesTabContainer
            categories={categories}
            editedValues={editedValues}
            onCommissionChange={handleCommissionChange}
            canManage={canManage}
            isLoading={isLoading}
            selectedId={selectedId}
            forcedOpenIds={forcedOpenIds}
            onSelect={handleSelect}
          />
        )}
        {activeTab === CommissionTab.HISTORY && <CommissionHistoryTabContainer />}
      </StyledContentBlock>
    </StyledPageWrapper>
  );
};

export default CommissionMainContainer;
